import React from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react';

/**
 * AccordionItemsField - Manages an array of accordion items
 *
 * @param {Object} props
 * @param {Object} props.field - Field configuration
 * @param {string} props.field.key - Unique field identifier
 * @param {string} props.field.label - Display label
 * @param {string} [props.field.description] - Optional description text
 * @param {Array} props.value - Array of accordion items
 * @param {Function} props.onChange - Callback when value changes
 * @param {boolean} [props.disabled] - Whether field is disabled
 *
 * Each accordion item has:
 * - id: string (unique identifier)
 * - title: string (header text)
 * - content: string (body content)
 * - defaultOpen: boolean (whether item is open by default)
 */
export default function AccordionItemsField({ field, value, onChange, disabled = false }) {
  const items = value || [];

  const handleAddItem = () => {
    const newItem = {
      id: Date.now().toString(),
      title: 'New Item',
      content: '',
      defaultOpen: false
    };
    onChange([...items, newItem]);
  };

  const handleRemoveItem = (index) => {
    // Prevent removing the last item
    if (items.length <= 1) return;
    const newItems = items.filter((_, i) => i !== index);
    onChange(newItems);
  };

  const handleUpdateItem = (index, fieldName, newValue) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [fieldName]: newValue };
    onChange(newItems);
  };

  const handleMoveItem = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= items.length) return;
    const newItems = [...items];
    [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
    onChange(newItems);
  };

  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-2">
        {field.label}
      </label>
      {field.description && (
        <p className="text-xs text-gray-500 mb-2">{field.description}</p>
      )}

      <div className="space-y-2">
        {items.map((item, index) => (
          <div
            key={item.id}
            className="border border-gray-200 rounded bg-gray-50 p-2"
          >
            {/* Item Header with reorder and delete controls */}
            <div className="flex items-center gap-1 mb-2">
              {/* Reorder buttons */}
              <div className="flex flex-col">
                <button
                  type="button"
                  onClick={() => handleMoveItem(index, -1)}
                  disabled={disabled || index === 0}
                  className={`p-0.5 rounded hover:bg-gray-200 ${
                    disabled || index === 0
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-gray-500'
                  }`}
                  title="Move up"
                >
                  <ChevronUp size={12} />
                </button>
                <button
                  type="button"
                  onClick={() => handleMoveItem(index, 1)}
                  disabled={disabled || index === items.length - 1}
                  className={`p-0.5 rounded hover:bg-gray-200 ${
                    disabled || index === items.length - 1
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-gray-500'
                  }`}
                  title="Move down"
                >
                  <ChevronDown size={12} />
                </button>
              </div>

              {/* Item number indicator */}
              <span className="text-xs text-gray-400 w-4">#{index + 1}</span>

              {/* Spacer */}
              <div className="flex-1" />

              {/* Delete button */}
              <button
                type="button"
                onClick={() => handleRemoveItem(index)}
                disabled={disabled || items.length <= 1}
                className={`p-1 rounded hover:bg-red-100 ${
                  disabled || items.length <= 1
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-red-500 hover:text-red-600'
                }`}
                title={items.length <= 1 ? 'Cannot delete the last item' : 'Delete item'}
              >
                <Trash2 size={14} />
              </button>
            </div>

            {/* Title input */}
            <div className="mb-2">
              <label className="block text-xs text-gray-500 mb-1">Title</label>
              <input
                type="text"
                value={item.title || ''}
                onChange={(e) => handleUpdateItem(index, 'title', e.target.value)}
                disabled={disabled}
                placeholder="Item title"
                className={`w-full px-2 py-1 text-xs border rounded bg-white ${
                  disabled
                    ? 'bg-gray-100 cursor-not-allowed border-gray-200'
                    : 'border-gray-200 focus:border-blue-400 focus:ring-1 focus:ring-blue-400'
                }`}
              />
            </div>

            {/* Content textarea */}
            <div className="mb-2">
              <label className="block text-xs text-gray-500 mb-1">Content</label>
              <textarea
                value={item.content || ''}
                onChange={(e) => handleUpdateItem(index, 'content', e.target.value)}
                disabled={disabled}
                rows={3}
                placeholder="Item content"
                className={`w-full px-2 py-1 text-xs border rounded resize-y bg-white ${
                  disabled
                    ? 'bg-gray-100 cursor-not-allowed border-gray-200'
                    : 'border-gray-200 focus:border-blue-400 focus:ring-1 focus:ring-blue-400'
                }`}
              />
            </div>

            {/* Default Open checkbox */}
            <label className={`flex items-center gap-2 cursor-pointer ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
              <input
                type="checkbox"
                checked={item.defaultOpen || false}
                onChange={(e) => handleUpdateItem(index, 'defaultOpen', e.target.checked)}
                disabled={disabled}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-xs text-gray-600">Default Open</span>
            </label>
          </div>
        ))}
      </div>

      {/* Add Item button */}
      <button
        type="button"
        onClick={handleAddItem}
        disabled={disabled}
        className={`mt-2 w-full flex items-center justify-center gap-1 px-2 py-1.5 text-xs border border-dashed rounded ${
          disabled
            ? 'border-gray-200 text-gray-400 cursor-not-allowed'
            : 'border-gray-300 text-gray-600 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50'
        }`}
      >
        <Plus size={14} />
        Add Item
      </button>
    </div>
  );
}
